import React, { useState, useEffect } from 'react';
import { 
  CircularProgress, 
  Grid, 
  Box, 
  Typography, 
  TextField as MuiTextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useHistory, useParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { pick } from 'lodash';
import useUser from '../lib/useUser';
import { apiFetch } from '../lib/fetch';
import Layout from '../components/layout';
import IncomePlanWrapper from '../components/income-plan-wrapper';
import { Button } from '../lib/common';
import { formatCurrency } from '../lib/util';
import { colors } from '../lib/styles';
import { toggleForm, generateAssetsByYear } from '../lib/client';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
);


// Maximum allowable Accumulation Interest % for illustration purposes — 6%, the
// top of the ARW (AnnuityRateWatch) range per Summit Compliance Group.
const MAX_ACCUM_RATE = 6;

const styles = {
  mainBody: {
    padding: '20px',
  },
  errTxt: {
    color: 'red',
  },
  tdCurrency: {
    textAlign: 'right',
    width: '100px',
    margin: '5px',
  },
}

const ClientAssetsYear = () => {
  const history = useHistory();
  const auth = useUser();
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [generatedClient, setGeneratedClient] = useState(null);
  const [err, setErr] = useState(null);
  const [planIncomeAfterTax, setPlanIncomeAfterTax] = useState('true');
  const [savingView, setSavingView] = useState(false);

  useEffect(() => {
    if (!auth?.id) return;
    apiFetch(`/users/${auth.id}/ip_households/${id}`)
    .then(async (res) => {
      setClient(res);
      const generated = await generateAssetsByYear(res);
      setGeneratedClient(generated);
    }).catch((e) => {
      let err = e.toString();
      setErr(err);
    });
  }, [auth?.id, id]);

  if (!client || !generatedClient) {
    return <Layout><div>Loading...</div></Layout>;
  }

  const {
    assetsByYearData: data,
    displayName,
    emergencyFunds,
  } = generatedClient;

  const handleSubmit = async () => {
    setErr(null);
    const toSave = pick(client, [
      'accum_rate',
      'assumed_net_growth',
    ]);
    try {
      const saved = await apiFetch.put(`/users/${auth.id}/ip_households/${id}`, toSave);
      saved.assets = client.assets;
      setClient(saved);
      const regenerated = await generateAssetsByYear(saved);
      setGeneratedClient(regenerated);
      return saved;
    } catch (e) {
      let err = e.toString();
      setErr(err);
    }
    return null;
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Total Projected Assets by Year',
        font: {
          size: 24,
          weight: 'bold',
        },
        color: colors.black,
      },
      tooltip: {
        color: colors.black,
        bodyFont: { size: 18 },
        titleFont: { size: 18 },
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return `$${value.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`,
          color: colors.black,
        },
      },
      x: {
        ticks: {
          color: colors.black,
        },
      },
    }
  };
  
  
  const chartData = {
    labels: data.map((row) => row.year),
    datasets: [
      {
        label: 'Projected Assets',
        data: data.map((row) => Number(row.yearTotalAssets).toFixed(2)),
        borderColor: colors.primaryColor,
        backgroundColor: 'rgba(142, 160, 196, 0.4)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  return (
    <IncomePlanWrapper handleSubmit={handleSubmit}>
      <Box style={styles.mainBody}>
        <div style={styles.errTxt}>{err?.toString()}</div>
        {client ? (
          <div style={{ margin: '10px' }}>
            <Grid container style={{ width: '100%' }}>
              <Grid item xs={6} id="client-edit-header">
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: { xs: 'nowrap', sm: 'wrap', md: 'nowrap' },
                    justifyContent: { xs: 'flex-start', sm: 'space-between' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    width: '100%',
                    padding: { xs: '10px', sm: '15px' },
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    gap: { xs: '10px', sm: '5px' }
                  }}
                >
                  <Box sx={{ 
                    width: '100%',
                    order: { xs: 1, sm: 1 },
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        fontWeight: 'bold',
                        color: colors.black
                      }}
                    >
                      {displayName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    gap: '8px',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    alignItems: 'center',
                    width: { xs: '100%', md: '75%' },
                    order: { xs: 2, sm: 2 },
                  }}>
                     <Button
                      endIcon={client.assets_year_forms ? <RemoveIcon /> : <AddIcon />}
                      variant='link'
                      disabled={savingView}
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        color: colors.white,
                        backgroundColor: colors.primaryColor,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '&:hover': {
                          color: colors.white,
                          backgroundColor: colors.primaryAccent,
                        }
                      }}
                      onClick={async () => {
                        setSavingView(true);
                        const updated = await toggleForm(auth, client, 'assets_year_forms', !client.assets_year_forms);
                        setClient(updated);
                        setSavingView(false);
                      }}
                    >
                      {client.assets_year_forms ? 'Hide' : 'Show'} Forms
                    </Button>
                  </Box>
                </Box>
              </Grid>
              
              {client.assets_year_forms &&
                <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: '#fff', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    p: 2,
                    height: '100%',
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', height: '100%', alignContent: 'center' }}>Accumulation Interest %:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MuiTextField
                          type="number"
                          size="small"
                          fullWidth
                          value={client.accum_rate || ''}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (val > MAX_ACCUM_RATE) {
                              val = MAX_ACCUM_RATE;
                            }
                            setClient({ ...client, accum_rate: val });
                          }}
                          helperText={`Max ${MAX_ACCUM_RATE}%`}
                          inputProps={{
                            style: { color: colors.black },
                            min: 0,
                            max: MAX_ACCUM_RATE,
                            step: 0.1
                          }}
                          sx={{ mb: 1 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: '#fff', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    p: 2,
                    height: '100%'
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', height: '100%', alignContent: 'center' }}>Recovery Growth %:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MuiTextField
                          type="number"
                          size="small"
                          fullWidth
                          value={client.assumed_net_growth || ''}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (val > 100) {
                              val = 100;
                            }
                            setClient({ ...client, assumed_net_growth: val });
                          }}
                          inputProps={{
                            style: { color: colors.black },
                            min: 0,
                            max: 100,
                            step: 0.1
                          }}
                          sx={{ mb: 1 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                </Grid>
              }
            </Grid>
            <Box sx={{ 
              overflowX: 'auto', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px',
              mb: 3,
              bgcolor: 'white'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                margin: '0 auto',
                border: '1px solid #fff'
              }}>
                <thead>
                  <tr>
                    {[
                      'Year',
                      `${client.firstname} Age`,
                      client.spouse_firstname ? `${client.spouse_firstname} Age` : 'Age2',
                      'Leg1',
                      'Leg2',
                      'Leg3',
                      'New Contributions',
                      'Growth / Recovery',
                      'Emergency',
                      'Total'
                    ].map((header, index) => (
                      <th key={index} style={{
                        padding: '10px',
                        backgroundColor: '#4F5F4B',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                      }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, index) => {
                    const tableCellStyle = {
                      padding: '8px 10px',
                      textAlign: 'center',
                      borderBottom: '1px solid #fff',
                      color: '#333',
                      whiteSpace: 'nowrap'
                    };
                    
                    const tableCellCenterStyle = {
                      ...tableCellStyle,
                      textAlign: 'center',
                    };
                    
                    return (
                      <tr key={index} style={{
                        backgroundColor: index % 2 === 0 ? '#fff' : '#e9ebff',
                      }}>
                        <td style={tableCellCenterStyle}>{row.year}</td>
                        <td style={tableCellCenterStyle}>{row.yearAge}</td>
                        <td style={tableCellCenterStyle}>{row.yearSpouseAge}</td>
                        <td style={tableCellStyle}>${formatCurrency(row.leg1Principal || "0.00")}</td>
                        <td style={tableCellStyle}>${formatCurrency(row.leg2Principal || "0.00")}</td>
                        <td style={tableCellStyle}>${formatCurrency(row.leg3Principal || "0.00")}</td>
                        <td style={tableCellStyle}>${formatCurrency(row.newContributions || "0.00")}</td>
                        <td style={tableCellStyle}>${formatCurrency(row.growthPrincipal || "0.00")}</td>
                        <td style={tableCellStyle}>${formatCurrency(emergencyFunds || "0.00")}</td>
                        <td style={tableCellStyle}>${formatCurrency(row.yearTotalAssets || "0.00")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
            <Box sx={{ flexGrow: 1, marginTop: '25px' }}>
              <Line options={options} data={chartData} />
            </Box>
          </div>
        ) : <CircularProgress />}
      </Box>
    </IncomePlanWrapper>
  );
};


export default ClientAssetsYear;
